/**
 * Invitations to join a node (backend routers/node_invites.py).
 *
 * useMyNodeInvites: GET /api/node-invites/mine — the invites waiting for the
 * signed-in person's e-mail, for nodes they are not in yet; what the node
 * switcher offers to join. Cached through the api client like /api/nodes/mine
 * (the nav bar mounts on every page); accepting one invalidates both.
 *
 * acceptNodeInvite(api, token): POST /api/node-invites/{token}/accept — from
 * any node. Resolves to { slug, name, url, is_current, admitted }: open `url`
 * (a full navigation, the node lives on its own address) unless is_current.
 *
 * useNodeInvites: superadmin — this node's invites (Node settings → Invites):
 * list, create, revoke.
 *
 * parseInviteToken(text): the token out of a pasted invite link, or a bare
 * token; null when the text is neither.
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

// Where an invite link lands (RouteMap.js: /join-node/:token).
const LINK_RE = /\/join-node\/([^/?#\s]+)/;
const TOKEN_RE = /^[A-Za-z0-9-]{8,64}$/;

export function parseInviteToken(text) {
    const s = (text || '').trim();
    if (!s) return null;
    const m = s.match(LINK_RE);
    if (m) {
        try { return decodeURIComponent(m[1]); } catch { return m[1]; }
    }
    return TOKEN_RE.test(s) ? s : null;
}

export function acceptNodeInvite(api, token) {
    // '/api/node' is the prefix of both cached lists this changes:
    // /api/nodes/mine (the person now belongs to one more node) and
    // /api/node-invites/mine (one fewer invite waiting).
    return api.post(`/api/node-invites/${encodeURIComponent(token)}/accept`, undefined, { invalidate: '/api/node' });
}

export function useMyNodeInvites() {
    const api = useApi();
    const { isAuthenticated } = useAuth();
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        if (!isAuthenticated) { setInvites([]); return Promise.resolve(); }
        setLoading(true);
        // A backend without the route, or a guest, yields no invites — never an error.
        return api.get('/api/node-invites/mine', { cache: true })
            .then(data => setInvites(Array.isArray(data) ? data : []))
            .catch(() => setInvites([]))
            .finally(() => setLoading(false));
    }, [api, isAuthenticated]);

    useEffect(() => { load(); }, [load]);

    const accept = useCallback(token => acceptNodeInvite(api, token).then(joined => {
        load();
        return joined;
    }), [api, load]);

    return { invites, loading, reload: load, accept };
}

export function useNodeInvites() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        if (!userId) return Promise.resolve();
        return api.get('/api/node-invites')
            .then(data => { setInvites(Array.isArray(data) ? data : []); setError(null); })
            .catch(err => { setInvites([]); setError(err); })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const create = useCallback(body => api.post('/api/node-invites', body).then(created => {
        setInvites(prev => [created, ...prev]);
        return created;
    }), [api]);

    const revoke = useCallback(id => api.del(`/api/node-invites/${id}`).then(updated => {
        setInvites(prev => prev.map(i => (i.id === updated.id ? updated : i)));
        return updated;
    }), [api]);

    return { invites, loading, error, create, revoke, reload: load };
}

export default useMyNodeInvites;
